import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CommonTextField from "../../components/CommonTextField";
import CommonButtons from "../../components/CommonButtons";
import PasswordFieldWithValidation from "./PasswordFieldWithValidation";
import { usePasswordValidation } from "../../hooks/usePasswordValidation";
import api from "../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "../../../assets/toastService";

const SetPassword: React.FC = () => {
  const { userid, expiryTime } = useParams<{ userid: string; expiryTime?: string }>();
  const navigate = useNavigate();
  const [createPassword, setCreatePassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const validation = usePasswordValidation(createPassword);
  const passwordMatch = confirmPassword.length > 0 && createPassword === confirmPassword;

  useEffect(() => {
    if (expiryTime) {
      const expiryInMs = parseInt(expiryTime, 10) * 1000;
      const nowInMs = Date.now();
      const timeDifference = expiryInMs - nowInMs;
      if (timeDifference <= 0) {
        navigate("/");
      } else {
        const countdown = setTimeout(() => navigate("/"), timeDifference);
        return () => clearTimeout(countdown);
      }
    }
  }, [expiryTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userid) {
      notifyError("An error occurred");
      return;
    }
    if (!validation.isValid) {
      notifyError("Please fix the password errors");
      return;
    }
    if (!passwordMatch) {
      notifyError("Passwords do not match");
      return;
    }
    try {
      await api.put(`/pos/update/${userid}`, { password: createPassword });
      notifySuccess("Password created successfully");
      navigate("/");
    } catch {
      notifyError("An error occurred");
    }
  };

  return (
    <div className='min-h-screen w-screen bg-[#EEF4F7] flex items-center justify-center'>
      <div className='w-full max-w-md bg-white rounded-xl shadow-md p-6'>
        <h1 className='text-2xl font-semibold mb-2'>Set Your Password</h1>
        <p className='text-sm text-gray-600 mb-6'>Create and confirm your new password.</p>

        <form onSubmit={handleSubmit} className='space-y-4'>
          {/* New Password */}
          <PasswordFieldWithValidation
            label='Create Password'
            value={createPassword}
            placeholder='Enter your new password'
            onChange={setCreatePassword}
          />
          {/* Confirm Password */}
          <CommonTextField
            label='Confirm Password'
            name='confirmPassword'
            type='password'
            value={confirmPassword}
            placeholder='Re-enter your password'
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            error={confirmPassword.length > 0 && !passwordMatch}
            helperText={
              confirmPassword.length > 0 ? (
                passwordMatch ? (
                  <span style={{ color: "green" }}>Passwords match</span>
                ) : (
                  <span style={{ color: "red" }}>Passwords do not match</span>
                )
              ) : (
                ""
              )
            }
          />
          <CommonButtons label='Set Password' type='submit' variant='primary' className='mx-auto justify-center py-3 mt-2' />
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SetPassword;
